import React, { useEffect, useState } from 'react';
import Bounce from 'react-reveal/Bounce';
import { getPromoProducts, getNonPromoProducts } from '../api/products';
import { getAllCategories } from '../api/category';
import {
  getAllSubcategories,
  getSubcategoriesByCategory,
  getCategoryOfSubcategory,
} from '../api/subcategory';
import useAuth from '../hooks/useAuth';
import useOrder from '../hooks/useOrder';
import swal from 'sweetalert';
import { useNavigate } from 'react-router-dom';
import { BsCart2 } from 'react-icons/bs';

const ProductsScreen = () => {
  const [products, setProducts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [filteredSubs, setFilteredSubs] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isRestoringState, setIsRestoringState] = useState(false);
  const [targetPage, setTargetPage] = useState(null);
  const [pageRestored, setPageRestored] = useState(false);
  const itemsPerPage = 40;

  const { user } = useAuth();
  const { handleCart } = useOrder();
  const navigate = useNavigate();

  useEffect(() => {
    const savedState = sessionStorage.getItem('productsScreenState');
    if (savedState) {
      setIsRestoringState(true);
    }
  }, []);

  useEffect(() => {
    if (selectedCategory && categories.length > 0 && subcategories.length > 0 && isRestoringState) {
      const restoreSubcategories = async () => {
        try {
          const res = await getSubcategoriesByCategory(selectedCategory);
          setFilteredSubs(res.data);
        } catch {
          setFilteredSubs(subcategories);
        }
      };
      restoreSubcategories();
    }
  }, [selectedCategory, categories, subcategories, isRestoringState]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [promoRes, nonPromoRes, catRes, subRes] = await Promise.all([
          getPromoProducts(),
          getNonPromoProducts(),
          getAllCategories(),
          getAllSubcategories(),
        ]);

        const now = new Date();
        const allProducts = [...promoRes.data, ...nonPromoRes.data]
          .filter((p) => p.quantity > 0)
          .map((p) => {
            const promoStart = new Date(p.promoStartDate);
            const isPromo = p.promoPercentage && promoStart <= now;
            return {
              ...p,
              isPromo,
              finalPrice: isPromo
                ? (p.initialPrice * (1 - p.promoPercentage / 100)).toFixed(2)
                : p.initialPrice.toFixed(2),
            };
          });

        setProducts(allProducts);
        setFiltered(allProducts);
        setCategories(catRes.data);
        setSubcategories(subRes.data);
        setFilteredSubs(subRes.data);

        const savedState = sessionStorage.getItem('productsScreenState');
        if (savedState) {
          const state = JSON.parse(savedState);
          setSearch(state.search || '');
          setSelectedCategory(state.selectedCategory || '');
          setSelectedSubcategory(state.selectedSubcategory || '');
          setTargetPage(state.currentPage || 1);
          if (state.filteredSubs && state.filteredSubs.length > 0) {
            setFilteredSubs(state.filteredSubs);
          }
          setTimeout(() => {
            setIsRestoringState(false);
            sessionStorage.removeItem('productsScreenState');
          }, 300);
        } else {
          setIsRestoringState(false);
        }
      } catch (err) {
        console.error('❌ Error fetching product data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (!loading) {
      const savedPosition = sessionStorage.getItem('productsScreenScrollPosition');
      if (savedPosition) {
        const scrollPos = parseInt(savedPosition, 10);
        setTimeout(() => {
          window.scrollTo({ top: scrollPos, behavior: 'smooth' });
        }, 100);
        sessionStorage.removeItem('productsScreenScrollPosition');
      }
    }
  }, [loading, filtered]);

  useEffect(() => {
    let result = [...products];
    if (search) {
      result = result.filter((p) =>
        p.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (selectedCategory) {
      result = result.filter(
        (p) =>
          (typeof p.category === 'string' && p.category === selectedCategory) ||
          (typeof p.category === 'object' && p.category?._id === selectedCategory)
      );
    }
    if (selectedSubcategory) {
      result = result.filter(
        (p) =>
          (typeof p.subcategory === 'string' && p.subcategory === selectedSubcategory) ||
          (typeof p.subcategory === 'object' && p.subcategory?._id === selectedSubcategory)
      );
    }
    setFiltered(result);

    if (isRestoringState && targetPage !== null && !pageRestored) {
      setCurrentPage(targetPage);
      setTargetPage(null);
      setPageRestored(true);
    } else if (!isRestoringState && !pageRestored && currentPage !== 1)
 {
      setCurrentPage(1);
    }
  }, [search, selectedCategory, selectedSubcategory, products, isRestoringState, targetPage, pageRestored]);

  const handleCategoryChange = async (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
    if (!categoryId) return setFilteredSubs(subcategories);
    try {
      const res = await getSubcategoriesByCategory(categoryId);
      setFilteredSubs(res.data);
    } catch {
      setFilteredSubs(subcategories);
    }
  };

  const handleSubcategoryChange = async (subcategoryId) => {
    setSelectedSubcategory(subcategoryId);
    if (!subcategoryId) return;
    try {
      const res = await getCategoryOfSubcategory(subcategoryId);
      const categoryId = res.data?._id;
      if (categoryId) {
        setSelectedCategory(categoryId);
        const subRes = await getSubcategoriesByCategory(categoryId);
        setFilteredSubs(subRes.data);
      }
    } catch {}
  };

  const handleAddToCart = async (productId) => {
    try {
      await handleCart({ _id: productId, quantity: 1 });
      swal('✅ Ajouté', 'Produit ajouté au panier', 'success');
    } catch {
      swal('❌ Erreur', 'Impossible d’ajouter au panier', 'error');
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filtered.slice(startIndex, startIndex + itemsPerPage);

  const renderPagination = () => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end > totalPages) {
      end = totalPages;
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`px-3 py-1 rounded-md text-sm font-medium ${
            i === currentPage
              ? 'bg-paragaia text-white'
              : 'bg-gray-200 text-paragaia hover:bg-paragaiaLight'
          }`}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex items-center justify-center gap-2 mt-10 flex-wrap">
        <button
          className="px-3 py-1 rounded-md bg-gray-200 text-paragaia hover:bg-paragaiaLight"
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        >
          &lt;
        </button>
        {pages}
        <button
          className="px-3 py-1 rounded-md bg-gray-200 text-paragaia hover:bg-paragaiaLight"
          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        >
          &gt;
        </button>
      </div>
    );
  };

  const renderCard = (product) => (
    <div className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition relative flex flex-col justify-between">
      {product.isPromo && (
        <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded-full shadow-md z-10">
          -{product.promoPercentage}%
        </span>
      )}
      <div className="w-full h-[230px] bg-gray-100 rounded overflow-hidden flex items-center justify-center mb-3">
        <img
          src={product.image}
          alt={product.name}
          className="max-h-full max-w-full object-contain"
        />
      </div>
      <h2 className="text-lg font-bold truncate">{product.name}</h2>
      {product.isPromo ? (
        <>
          <p className="text-gray-500 line-through">{product.initialPrice} TND</p>
          <p className="text-paragaia text-xl font-semibold">{product.finalPrice} TND</p>
        </>
      ) : (
        <p className="text-paragaia text-xl font-semibold">{product.initialPrice} TND</p>
      )}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-2">
        {user && (
          <button
            className="bg-paragaia text-white px-3 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-paragaiaLight w-full sm:w-auto"
            onClick={() => handleAddToCart(product._id)}
          >
            <BsCart2 />
            Ajouter
          </button>
        )}
        <button
          className="border border-paragaia text-paragaia px-3 py-2 rounded-md text-sm hover:bg-paragaiaLight w-full sm:w-auto"
          onClick={() => {
            const currentState = {
              search,
              selectedCategory,
              selectedSubcategory,
              currentPage,
              filteredSubs,
            };
            sessionStorage.setItem('productsScreenState', JSON.stringify(currentState));
            sessionStorage.setItem('productsScreenScrollPosition', window.scrollY.toString());
            localStorage.setItem('selected_product_id', product._id);
            localStorage.setItem('selected_product_promo', product.promoPercentage);
            navigate(`/products/view/${encodeURIComponent(product.name)}`);
          }}
        >
          Voir
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-white">
        <img src="/assets/loading.gif" alt="Chargement..." className="w-40 h-40" />
      </div>
    );
  }

  return (
    <section className="max-w-screen-xl py-20 mx-auto px-6">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between mb-8">
        <h1 className="text-2xl font-bold text-paragaia text-right w-full md:w-auto">
          🍭 Tous nos Produits
        </h1>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 w-full md:w-auto">
          <input
            type="text"
            placeholder="🔍 Rechercher un produit..."
            className="border border-paragaia text-sm text-paragaia rounded-md px-4 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-paragaiaLight bg-paragaiaLight placeholder-paragaia"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
            className="border border-paragaia text-sm text-paragaia rounded-md px-4 py-2 w-full sm:w-60 bg-paragaiaLight focus:outline-none focus:ring-2 focus:ring-paragaiaLight"
          >
            <option value="">Toutes les catégories</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>{cat.name}</option>
            ))}
          </select>
          <select
            value={selectedSubcategory}
            onChange={(e) => handleSubcategoryChange(e.target.value)}
            className="border border-paragaia text-sm text-paragaia rounded-md px-4 py-2 w-full sm:w-60 bg-paragaiaLight focus:outline-none focus:ring-2 focus:ring-paragaiaLight"
          >
            <option value="">Tous les sous-catégories</option>
            {filteredSubs.map((sub) => (
              <option key={sub._id} value={sub._id}>{sub.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8 md:gap-10 py-6">
        {paginatedProducts.map((product) => (
          <Bounce left key={product._id}>
            {renderCard(product)}
          </Bounce>
        ))}
      </div>

      {renderPagination()}
    </section>
  );
};

export default ProductsScreen;